import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Calendar, FileCheck, Building2, Mail, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import DashboardLayout from '../../components/DashboardLayout';


export default function PartnerAcquisition() {
  const navigate = useNavigate();

  // Add this block - State declarations for modal and user creation
  const [selectedApplication, setSelectedApplication] = React.useState<any>(null);
  const [isCreatingUser, setIsCreatingUser] = React.useState(false);
  const [userCreationError, setUserCreationError] = React.useState<string | null>(null);
  const [userCreationSuccess, setUserCreationSuccess] = React.useState<string | null>(null);

  // Add this block - Mock functions for creating partner account and fetching applications
  const createPartnerAccount = async (applicationId: string) => {
    // Placeholder implementation
    return { success: true, message: 'Partner account created successfully' };
  };

  const fetchApplications = async () => {
    // Placeholder implementation
    console.log('Fetching applications...');
  };
  // End of added block

  const handleCreateUser = async () => {
    if (!selectedApplication) return;

    setIsCreatingUser(true);
    setUserCreationError(null);
    setUserCreationSuccess(null);

    try {
      const result = await createPartnerAccount(selectedApplication.id);

      if (result.success) {
        setUserCreationSuccess(result.message);
        // Refresh data to show updated status
        await fetchApplications();
        
        // Show success for 5 seconds before closing modal
        setTimeout(() => {
          setIsCreatingUser(false);
          setSelectedApplication(null);
          setUserCreationSuccess(null);
        }, 5000);
      } else {
        setUserCreationError(result.message);
        setIsCreatingUser(false);
      }
    } catch (error: any) {
      console.error('Error creating partner account:', error);
      setUserCreationError(error.message || 'Failed to create partner account');
      setIsCreatingUser(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Partner Acquisition</h1>
            <p className="text-gray-600">Manage new partner acquisition and onboarding workflows</p>
          </div>

          {/* Main Action Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Partner Onboarding Workflow Card */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg shadow-lg p-8 hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-600 rounded-full">
                  <FileCheck className="w-8 h-8 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-sm text-purple-600 font-semibold">Workflow Management</p>
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Partner Onboarding</h2>
              <p className="text-gray-600 mb-6">
                Review and process partner applications through the complete onboarding workflow - from application review to account creation.
              </p>
              
              <div className="space-y-2 mb-6">
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                  <span>Application review and pre-approval</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                  <span>Onboarding call scheduling</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                  <span>Contract management</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                  <span>Account creation and activation</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/partner-onboarding-workflow')}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg"
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Manage Applications
              </Button>
            </div>

            {/* Onboarding Calls Card */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-lg shadow-lg p-8 hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-600 rounded-full">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-sm text-blue-600 font-semibold">Schedule Management</p>
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Onboarding Calls</h2>
              <p className="text-gray-600 mb-6">
                View and manage all scheduled onboarding calls with potential partners in an organized calendar interface.
              </p>
              
              <div className="space-y-2 mb-6">
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                  <span>Calendar view of scheduled calls</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                  <span>Partner contact information</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                  <span>Meeting status tracking</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                  <span>Quick access to partner details</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/partner-onboarding-calls')}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg"
              >
                <Calendar className="w-5 h-5 mr-2" />
                View Call Calendar
              </Button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Applications</p>
                  <p className="text-2xl font-bold text-purple-600">--</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <FileCheck className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Upcoming Calls</p>
                  <p className="text-2xl font-bold text-blue-600">--</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">In Progress</p>
                  <p className="text-2xl font-bold text-yellow-600">--</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <UserPlus className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-green-600">--</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create User Modal */}
        {selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Create Partner Account
              </h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Business Name</p>
                  <p className="font-semibold text-gray-900">{selectedApplication.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contact Email</p>
                  <p className="font-semibold text-gray-900">{selectedApplication.contact_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contact Name</p>
                  <p className="font-semibold text-gray-900">{selectedApplication.contact_name}</p>
                </div>

                {/* Info box about magic link */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Magic Link Authentication</p>
                      <p className="text-sm text-blue-700 mt-1">
                        A secure login link will be sent to the partner's email. They can click this link to set up their account and access the partner dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {userCreationError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">Error Creating Account</p>
                      <p className="text-sm text-red-700 mt-1">{userCreationError}</p>
                    </div>
                  </div>
                </div>
              )}

              {userCreationSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900">Account Created Successfully</p>
                      <p className="text-sm text-green-700 mt-1">{userCreationSuccess}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedApplication(null);
                    setUserCreationError(null);
                    setUserCreationSuccess(null);
                  }}
                  disabled={isCreatingUser}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {userCreationSuccess ? 'Close' : 'Cancel'}
                </button>
                {!userCreationSuccess && (
                  <button
                    onClick={handleCreateUser}
                    disabled={isCreatingUser}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {isCreatingUser ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Sending Magic Link...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Magic Link
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}